import { ShareButton, shareUrlFor, storyUrlFor, useShareableUrl } from "@/components/ShareButton";
import { PageLoading, LoadError } from "@/components/LoadState";
import { useCatalog, similarTo } from "@/context/CatalogContext";
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, coverUrl, downloadTrackUrl, fmtDuration, packTrackToPlayable, buyItemFor } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { Play, Pause, Download, ShoppingCart } from "lucide-react";
import TrackCard from "@/components/TrackCard";
import BizumModal from "@/components/BizumModal";
import { GenrePill } from "@/components/GenrePill";
import { TrackStats } from "@/components/TrackStats";
import { StarRating } from "@/components/StarRating";
import { VersionActions } from "@/components/VersionActions";
import { trackVersionsPlayable, queueAfter } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function TrackDetail({ config, pista = false }) {
  const { id } = useParams();
  useShareableUrl(pista ? "pista" : "track", id);
  const catalog = useCatalog();
  const [track, setTrack] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [bizumOpen, setBizumOpen] = useState(false);
  const p = usePlayer();
  const auth = useAuth();

  const [missing, setMissing] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Instant path: resolve from the preloaded catalog
    if (pista) {
      const hit = catalog.findPista(id);
      if (hit) {
        const t = hit.pista.track_id ? catalog.getTrack(hit.pista.track_id) : null;
        if (t) { setTrack(t); setSimilar(similarTo(t, catalog.tracks)); return; }
        setTrack(packTrackToPlayable(hit.pack, hit.pista)); setSimilar([]); return;
      }
    } else {
      const t = catalog.getTrack(id);
      if (t) { setTrack(t); setSimilar(similarTo(t, catalog.tracks)); return; }
    }
    (async () => {
      try {
        if (pista) {
          const r = await api.get(`/pistas/${id}`);
          const pt = r.data.pista;
          if (pt.track_id) { const t = await api.get(`/tracks/${pt.track_id}`).catch(() => null); if (t) { setTrack(t.data); setSimilar((await api.get(`/tracks/${pt.track_id}/similar?limit=8`).catch(() => ({ data: [] }))).data); return; } }
          setTrack(packTrackToPlayable({ ...r.data.pack, tracks: new Array(r.data.pack_tracks).fill(0) }, pt));
          setSimilar([]);
          return;
        }
        const [t, s] = await Promise.all([
          api.get(`/tracks/${id}`),
          api.get(`/tracks/${id}/similar?limit=8`).catch(() => ({ data: [] })),
        ]);
        setTrack(t.data);
        setSimilar(s.data);
      } catch (e) { if (e?.response?.status === 404) setMissing(true); else setFailed(true); }
    })();
    // eslint-disable-next-line
  }, [id, pista, attempt, catalog.tracks, catalog.packs]);

  if (failed) return <div className="max-w-6xl mx-auto px-5 py-24"><LoadError onRetry={() => { setFailed(false); setAttempt((a) => a + 1); }} testid="track-error" /></div>;
  if (missing) return (
    <div className="max-w-7xl mx-auto px-5 py-24 text-center" data-testid="track-not-found">
      <h1 className="font-display font-black uppercase text-3xl mb-3">Pista no encontrada</h1>
      <p className="text-white/50 mb-6">Puede que se haya eliminado o movido.</p>
      <Link to="/pool" className="btn-red text-white px-6 py-3 rounded-full font-semibold">Ir al Pool</Link>
    </div>
  );
  if (!track) return <div className="max-w-6xl mx-auto px-5 py-24"><PageLoading rows={3} label="Cargando tema…" testid="track-loading" /></div>;

  const isCurrent = p?.current?.id === track.id;
  const isPlaying = isCurrent && p?.isPlaying;

  return (
    <div>
      <section className="relative overflow-hidden border-b border-white/5" data-testid="track-hero">
        {track.cover_path ? (
          <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
            <img src={coverUrl(track.cover_path, 160)} alt="" className="w-full h-full object-cover scale-125 blur-3xl opacity-60 saturate-150" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/55 to-[#0a0a0f]" />
          </div>
        ) : <div className="absolute inset-0 ambient-red pointer-events-none" />}
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-14 grid md:grid-cols-2 gap-10 items-center">
          <div className="relative aspect-square rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/60">
            {track.cover_path ? (
              <img src={coverUrl(track.cover_path, 1200)} alt={track.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-red-900/40 to-black flex items-center justify-center">
                <span className="font-display font-black text-9xl text-red-600/40">{track.title?.[0]}</span>
              </div>
            )}
          </div>

          <div>
            <div className="font-mono text-xs text-red-500 uppercase tracking-[0.3em] mb-3">
              {{ remix: "Remix", mashup: "Mashup", original: "Original", pool: "Pool" }[track.category] || "Track"}
            </div>
            <h1 className="font-display font-black text-3xl sm:text-5xl uppercase tracking-tight leading-none text-white">
              {track.title}
            </h1>
            <p className="text-white text-lg mt-3">{track.artist}</p>
            {track.pack_id && <Link to={`/pack/${track.pack_id}`} data-testid="track-detail-pack-link" className="inline-flex items-center gap-2 mt-3 font-mono text-[10px] uppercase tracking-widest px-3 py-1 rounded-full border text-sky-400 border-sky-800/60 bg-sky-950/40 hover:bg-sky-900/50 hover:text-white">Del pack {track.pack_title ? `«${track.pack_title}»` : ""} · ver pack</Link>}
            <StarRating track={track} size="lg" className="mt-4" />
            {config?.show_stats && <TrackStats track={track} className="mt-2 !text-xs" />}


            <div className="flex flex-wrap gap-2 mt-6">
              {track.genre && <GenrePill genre={track.genre} className="!text-xs !px-3 !py-1" />}
              {track.bpm && <Pill red>{track.bpm} BPM</Pill>}
              {track.duration && <Pill testid="track-detail-duration">{fmtDuration(track.duration)}</Pill>}
              {(track.tags || []).map((tag) => <Pill key={tag}>{tag}</Pill>)}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid={`detail-play-${track.id}`}
                onClick={() => p.playTrack(track, similar)}
                className="btn-red text-white px-6 py-3 rounded-full font-semibold flex items-center gap-2">
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                {isPlaying ? "Pausar" : "Reproducir"}
              </button>

              {track.price === 0 ? (
                <a href={track.download_url || downloadTrackUrl(track.id)} onClick={(e) => auth.requireAuth(e)}
                  data-testid={`detail-download-${track.id}`}
                  className="px-6 py-3 rounded-full border border-white/15 hover:border-red-600/60 hover:text-red-500 font-semibold text-white/80 flex items-center gap-2">
                  <Download className="w-4 h-4" /> Descargar gratis
                </a>
              ) : (
                <button onClick={() => setBizumOpen(true)}
                  data-testid={`detail-buy-${track.id}`}
                  className="px-6 py-3 rounded-full border border-red-600/60 text-red-500 hover:bg-red-600/10 font-semibold flex items-center gap-2">
                  <ShoppingCart className="w-4 h-4" /> Comprar €{track.price.toFixed(2)}
                </button>
              )}
              <ShareButton url={shareUrlFor(track)} storyUrl={storyUrlFor(track)} title={track.title} testid={`track-detail-share-${track.id}`} label="Compartir"
                className="px-5 py-3 rounded-full border border-white/15 hover:border-red-600/60 font-semibold text-white/80" />
            </div>
          </div>
        </div>
      </section>

      {(track.versions || []).length > 0 && (
        <section className="max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="track-versions">
          <div className="mb-6">
            <div className="font-mono text-xs text-red-500 uppercase tracking-[0.3em] mb-2">Versiones</div>
            <h2 className="font-display font-black text-3xl uppercase tracking-tight text-white">Elige tu versión</h2>
          </div>
          <div className="divide-y divide-white/5 border border-white/5 rounded-2xl overflow-hidden bg-[#111116]">
            {trackVersionsPlayable(track).map((v, i) => {
              const isCur = p?.current?.id === v.id;
              const vPlaying = isCur && p?.isPlaying;
              const all = trackVersionsPlayable(track);
              return (
                <div key={v.id} data-testid={`track-version-row-${v.id}`} onClick={() => p.playTrack(v, queueAfter(all, v.id))}
                  className={`flex items-center gap-4 px-4 sm:px-6 py-3 cursor-pointer select-none transition-colors ${isCur ? "bg-red-950/20" : "hover:bg-white/[0.03]"}`}>
                  <span className="font-mono text-xs text-white/30 w-6 text-right">{String(i + 1).padStart(2, "0")}</span>
                  <button onClick={(e) => { e.stopPropagation(); p.playTrack(v, queueAfter(all, v.id)); }} data-testid={`track-version-play-${v.id}`}
                    className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isCur ? "btn-red text-white" : "bg-white/5 hover:bg-red-600/30 text-white"}`}>
                    {vPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className={`text-sm font-semibold truncate ${isCur ? "text-red-400" : "text-white"}`}>{v.version_title}</div>
                    <div className="text-xs text-white/70 truncate">{v.artist}</div>
                  </div>
                  <span className="font-mono text-xs text-white/40 w-10 text-right">{fmtDuration(v.duration)}</span>
                  <VersionActions track={track} version={v} config={config} />
                </div>
              );
            })}
          </div>
        </section>
      )}

      {similar.length > 0 && (
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
          <div className="mb-8">
            <div className="font-mono text-xs text-red-500 uppercase tracking-[0.3em] mb-2">Recomendaciones</div>
            <h2 className="font-display font-black text-3xl uppercase tracking-tight text-white">Temas similares</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {similar.map((t) => <TrackCard key={t.id} track={t} queue={similar} config={config} />)}
          </div>
        </section>
      )}

      <BizumModal open={bizumOpen} onClose={() => setBizumOpen(false)} {...buyItemFor(track)} config={config} />
    </div>
  );
}

function Pill({ children, red, testid }) {
  return (
    <span data-testid={testid} className={`font-mono text-xs uppercase tracking-widest px-3 py-1 rounded-full border ${
      red ? "text-sky-400 border-sky-800/50 bg-sky-950/40" : "text-white/60 border-white/10"
    }`}>{children}</span>
  );
}
